import {
    IFPGrowthLineSvg,
    IFPGrowthNode,
    IFPGrowthNodeSvg,
    IFPGrowthParameters,
    IFPGrowthResult,
    IFPGrowthTreeSvg
} from '../fp-growth-interfaces';
import { v4 as uuidv4 } from 'uuid';
import {
    createFrequencyGroupsPermutations,
    getAssociationRules,
    removeSelfRepetitionRules
} from '../../apriori/domain/apriori-provider';

const getItemsWithMinCount = (minCount: number, rows: string[][]): Map<string, number> => {
    const map: Map<string, number> = new Map();

    for(const row of rows) {
        for(const letter of row) {
            const count = map.get(letter) || 0;
            map.set(letter, count + 1);

        }
    }

    const filteredMap: Map<string, number> = new Map();

    for(const [letter, count] of map) {
        if(count >= minCount) {
            filteredMap.set(letter, count);
        }
    }

    return filteredMap;
};

const clearTable = (rows: string[][], map: Map<string, number>): string[][] => {
    const filtered: string[][] = [];

    for(const row of rows) {

        const newRow: string[] = [];

        for(const letter of row) {
            if(map.has(letter)) {
                newRow.push(letter);
            }
        }

        newRow.sort((a, b) => {
            const count1 = map.get(a) ?? 0;
            const count2 = map.get(b) ?? 0;

            if(count1 !== count2) return count2 - count1;

            return a < b ? -1 : 1;
        });

        filtered.push(newRow);
    }

    return filtered;
};

const buildPath = (row: string[], index: number, parentNode: IFPGrowthNode) => {
    if(index >= row.length) return;

    const letter = row[index];

    const nested = parentNode.nodes || [];
    const foundIndex = nested.findIndex(item => item.letter === letter);
    if(foundIndex === -1) {
        // create new and continue ----------------
        const newNode: IFPGrowthNode = {
            letter,
            count: 1,
            nodes: [],
            id: uuidv4(),
            parentId: parentNode.id,
        };
        nested.push(newNode);
        buildPath(row, index + 1, newNode);
    }
    else{
        // update count and continue ---------------
        const currentNode = nested[foundIndex];
        currentNode.count++;
        buildPath(row, index + 1, currentNode);
    }
};

const buildTree = (table: string[][], root: IFPGrowthNode) => {

    for(let i=0; i<table.length; i++) {
        const row = table[i];
        buildPath(row, 0, root);
    }
};

const buildTreeSvg = (root: IFPGrowthNode) : IFPGrowthTreeSvg => {
    const queue: IFPGrowthNode[] = [root];
    const svgNodes: IFPGrowthNodeSvg[] = [];
    const lines: IFPGrowthLineSvg[] = [];

    const MARGIN_X = 50;
    const MARGIN_Y = 50;
    const NODE_WIDTH = 100;
    const NODE_HEIGHT = 70;

    // let level = 0;
    let x = MARGIN_X;
    let y = MARGIN_Y;
    let maxX = MARGIN_X;

    svgNodes.push({
        letter: 'Root',
        count: 0,
        x,
        y,
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        isRoot: true,
        id: uuidv4(),
        parentId: uuidv4(),
    });

    y += NODE_HEIGHT + MARGIN_Y;

    while (queue.length > 0) {

        let levelSize = queue.length;

        while (levelSize > 0) {
            const node = queue.shift() as IFPGrowthNode;
            // result.push(node.val);

            const nested = node.nodes || [];
            for(const nestedNode of nested) {
                queue.push(nestedNode);
            }

            levelSize--;
        }

        x = MARGIN_X;

        // const totalRowWidth = queue.length * NODE_WIDTH + (queue.length - 1) * MARGIN_X;

        for(let i=0; i<queue.length; i++) {
            const node = queue[i];
            svgNodes.push({
                letter: node.letter,
                count: node.count,
                x,
                y,
                width: NODE_WIDTH,
                height: NODE_HEIGHT,
                isRoot: false,
                id: node.id,
                parentId: node.parentId,
            });

            const parentNode = svgNodes.find(item => item.id === node.parentId);

            lines.push({
                x1: (parentNode?.x ?? MARGIN_X) + NODE_WIDTH/2,
                y1: (parentNode?.y ?? MARGIN_Y) + NODE_HEIGHT,
                x2: x + NODE_WIDTH/2,
                y2: y,
            })

            x += NODE_WIDTH + MARGIN_X;
            maxX = Math.max(x, maxX);
        }

        y += NODE_HEIGHT + MARGIN_Y;

        // console.log(level, queue.map(item => item.letter));
        // level++;
    }

    y += MARGIN_Y;

    return {
        nodes: svgNodes,
        lines,
        width: maxX,
        height: y,
    }
};

const getFrequencyGroups = (table: string[][], minCount: number): string[] => {
    const counter: Map<string, number> = new Map();

    for (const row of table) {
        const unique = [...new Set(row)];
        const n = unique.length;

        // Generate all subsets of size 1 and 2 (you can increase this to 3+)
        for (let i = 0; i < n; i++) {
            const a = unique[i];
            const key1 = a;
            counter.set(key1, (counter.get(key1) ?? 0) + 1);

            for (let j = i + 1; j < n; j++) {
                const b = unique[j];
                const key2 = [a, b].sort().join(',');
                counter.set(key2, (counter.get(key2) ?? 0) + 1);
            }
        }
    }

    const result: string[] = [];

    for (const [key, count] of counter.entries()) {
        if (count >= minCount) {
            result.push(key);
        }
    }

    result.sort((a, b) => {
        if(a.length === b.length) return a.localeCompare(b);

        return a.length - b.length;
    });
    return result;
};

export const calculateFPGrowth = (params: IFPGrowthParameters): IFPGrowthResult => {

    const results: IFPGrowthResult = {
        minCount: params.minSupport * params.rows.length,
        lettersMap: [],
        cleanedTable: [],
        tree: {
            letter: 'Root',
            count: 0,
            nodes: [],
            id: uuidv4(),
            parentId: null,
        },
        treeSvg: null,
        frequencyGroups: [],
        associationRules: [],
    }

    const map = getItemsWithMinCount(results.minCount, params.rows);
    const arr = [...map];
    arr.sort((a, b) => {
        const [letter1, count1] = a;
        const [letter2, count2] = b;

        if(count1 !== count2) return count2 - count1;
        return letter1 < letter2 ? -1 : 1;
    });
    results.lettersMap = arr;

    results.cleanedTable = clearTable(params.rows, map);

    buildTree(results.cleanedTable, results.tree);

    results.treeSvg = buildTreeSvg(results.tree);

    results.frequencyGroups = getFrequencyGroups(results.cleanedTable, results.minCount);

    const permutations = createFrequencyGroupsPermutations(results.frequencyGroups);
    const filteredPermutations = removeSelfRepetitionRules(permutations);
    results.associationRules = getAssociationRules(filteredPermutations, results.cleanedTable.map(item => item.join('')), params.minConfidence);

    for(const rule of results.associationRules) {
        if(rule.isStrong) {
            rule.isStrong = rule.confidence.topSupport.result > params.minSupport;
        }
    }

    return results;
};